import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, Pencil, Plus, Tag, Trash2 } from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { usePermission } from '@/features/permissions/usePermission'
import { TAG_COLORS } from '@/lib/tag-colors'
import type { Label } from '@/types/entities'


export function TagChip({ label, size = 'xs', className }: { label: Label; size?: 'xs' | 'sm'; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm border font-medium whitespace-nowrap',
        size === 'xs' ? 'h-5 px-1.5 text-micro' : 'h-6 px-2 text-tiny',
        className,
      )}
      style={{ borderColor: label.color, color: label.color, backgroundColor: `${label.color}14` }}
    >
      <span className="size-1.5 rounded-full" style={{ backgroundColor: label.color }} aria-hidden />
      {label.name}
    </span>
  )
}

/**
 * Pick tags for an item, and add a new one in place when you may manage labels.
 *
 * Tags are workspace-wide (the labels table). Creating one with a name that already exists
 * returns the existing tag, so two people typing "urgent" get one tag, not two.
 */
export function TagPicker({
  workspaceId,
  selected,
  onChange,
  disabled,
}: {
  workspaceId: string
  selected: string[]
  onChange: (labelIds: string[]) => void
  disabled?: boolean
}) {
  const { can } = usePermission()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [color, setColor] = useState<string>(TAG_COLORS[0])
  const [editing, setEditing] = useState<Label | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const labelsQuery = useQuery({
    queryKey: qk.labels(workspaceId),
    queryFn: () => apiGet<{ items: Label[] }>('fields/labels', { workspaceId }),
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  })
  const labels = labelsQuery.data?.items ?? []
  const chosen = labels.filter((l) => selected.includes(l.id))

  const create = useMutation({
    mutationFn: () => apiPost<Label>('fields/labels', { workspaceId, boardId: null, name: draft.trim(), color }),
    onSuccess: (label) => {
      void queryClient.invalidateQueries({ queryKey: qk.labels(workspaceId) })
      if (!selected.includes(label.id)) onChange([...selected, label.id])
      setDraft('')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  // Renames and recolours reach every item carrying the tag, so the board and open item
  // refresh too; a delete also pulls the tag out of this item's selection.
  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: qk.labels(workspaceId) })
    void queryClient.invalidateQueries({ queryKey: ['board'] })
    void queryClient.invalidateQueries({ queryKey: ['card'] })
  }
  const save = useMutation({
    mutationFn: (vars: { id: string; name: string; color: string }) =>
      apiPatch<Label>(`fields/labels/${vars.id}`, { name: vars.name.trim(), color: vars.color }),
    onSuccess: () => {
      refreshAll()
      setEditing(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`fields/labels/${id}`),
    onSuccess: (_r, id) => {
      refreshAll()
      if (selected.includes(id)) onChange(selected.filter((x) => x !== id))
      setEditing(null)
      setConfirmDelete(null)
      toast.success('Tag deleted from every item')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const mayManage = can('manage', 'Label')

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chosen.map((l) => (
        <TagChip key={l.id} label={l} size="sm" />
      ))}
      {chosen.length === 0 && disabled ? <span className="text-sm text-text-muted">None</span> : null}
      {!disabled ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-tiny">
              <Tag className="size-3" />
              {chosen.length ? 'Edit tags' : 'Add tags'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2">
            <div className="max-h-56 overflow-y-auto">
              {labels.length === 0 ? (
                <p className="px-1 py-2 text-tiny text-text-muted">No tags yet.</p>
              ) : (
                labels.map((l) => {
                  const on = selected.includes(l.id)
                  if (editing?.id === l.id) {
                    return (
                      <form
                        key={l.id}
                        className="rounded-sm border border-brand bg-brand-softer p-1.5"
                        onSubmit={(e) => {
                          e.preventDefault()
                          if (editing.name.trim()) save.mutate({ id: l.id, name: editing.name, color: editing.color })
                        }}
                      >
                        <Input
                          autoFocus
                          value={editing.name}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          maxLength={40}
                          className="h-7 text-xs"
                        />
                        <div className="mt-1.5 flex items-center gap-1">
                          {TAG_COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              aria-label={`Colour ${c}`}
                              aria-pressed={editing.color === c}
                              onClick={() => setEditing({ ...editing, color: c })}
                              className={cn('size-4 rounded-full border-2', editing.color === c ? 'border-foreground' : 'border-transparent')}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </div>
                        <div className="mt-2 flex items-center gap-1">
                          <Button type="submit" size="sm" className="h-6 px-2 text-tiny" disabled={!editing.name.trim() || save.isPending}>
                            Save
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-tiny" onClick={() => { setEditing(null); setConfirmDelete(null) }}>
                            Cancel
                          </Button>
                          <span className="flex-1" />
                          {confirmDelete === l.id ? (
                            <Button type="button" size="sm" variant="destructive" className="h-6 px-2 text-tiny" disabled={remove.isPending} onClick={() => remove.mutate(l.id)}>
                              Delete from all items
                            </Button>
                          ) : (
                            <Button type="button" size="sm" variant="ghost" className="h-6 gap-1 px-2 text-tiny text-danger hover:text-danger" onClick={() => setConfirmDelete(l.id)}>
                              <Trash2 className="size-3" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </form>
                    )
                  }
                  return (
                    <div key={l.id} className="group flex items-center rounded-sm hover:bg-muted">
                      <button
                        type="button"
                        onClick={() => toggle(l.id)}
                        className="flex min-w-0 flex-1 items-center gap-2 px-1.5 py-1.5 text-left text-sm"
                      >
                        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: l.color }} aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{l.name}</span>
                        {on ? <Check className="size-3.5 text-brand" /> : null}
                      </button>
                      {mayManage ? (
                        <button
                          type="button"
                          aria-label={`Edit tag ${l.name}`}
                          onClick={() => { setEditing(l); setConfirmDelete(null) }}
                          className="mr-1 rounded-sm p-1 text-text-muted opacity-0 group-hover:opacity-100 hover:bg-card hover:text-foreground focus-visible:opacity-100"
                        >
                          <Pencil className="size-3" />
                        </button>
                      ) : null}
                    </div>
                  )
                })
              )}
            </div>
            {mayManage && !editing ? (
              <form
                className="mt-2 border-t border-border-soft pt-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (draft.trim()) create.mutate()
                }}
              >
                <div className="flex items-center gap-1.5">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="New tag"
                    maxLength={40}
                    className="h-7 text-xs"
                  />
                  <Button type="submit" size="icon" className="size-7 shrink-0" disabled={!draft.trim() || create.isPending} aria-label="Add tag">
                    <Plus className="size-3.5" />
                  </Button>
                </div>
                <div className="mt-1.5 flex items-center gap-1">
                  {TAG_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={`Colour ${c}`}
                      aria-pressed={color === c}
                      onClick={() => setColor(c)}
                      className={cn('size-4 rounded-full border-2', color === c ? 'border-foreground' : 'border-transparent')}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </form>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  )
}
